import React, { useState, useEffect } from 'react';
import { Tenant } from '../types';
import { api } from '../services/api';
import { X, User, Phone, Mail, FileText, Calendar, Briefcase, Paperclip, Eye, Download, ShieldAlert, Loader2 } from 'lucide-react';

const openBlob = (dataUrl: string, fileName: string, action: 'view' | 'download') => {
    try {
        const arr = dataUrl.split(',');
        if (arr.length < 2) {
            if (dataUrl.startsWith('http') || dataUrl.startsWith('blob:')) {
                if (action === 'view') {
                    window.open(dataUrl, '_blank');
                } else {
                    const link = document.createElement('a');
                    link.href = dataUrl;
                    link.download = fileName;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                }
                return;
            }
            throw new Error("تنسيق الملف غير صالح");
        }
        const mime = arr[0].match(/:(.*?);/)?.[1] || '';
        const bstr = atob(arr[1]);
        let n = bstr.length;
        const u8arr = new Uint8Array(n);
        while (n--) {
            u8arr[n] = bstr.charCodeAt(n);
        }
        const blob = new Blob([u8arr], { type: mime });
        const url = URL.createObjectURL(blob);
        if (action === 'view') {
            window.open(url, '_blank');
        } else {
            const link = document.createElement('a');
            link.href = url;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
        setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e: any) {
        alert("❌ تعذر معالجة الملف: " + e.message);
    }
};

interface TenantDetailsDialogProps {
  tenant: Tenant;
  onClose: () => void;
}

const DetailItem = ({ label, value, icon: Icon }: { label: string; value: string | React.ReactNode; icon: any }) => (
  <div className="flex items-start gap-3 p-3.5 bg-slate-50 border border-slate-100 rounded-xl">
    <div className="bg-white p-2 rounded-lg border border-slate-200 text-slate-400 shrink-0">
      <Icon size={16} />
    </div>
    <div>
      <div className="text-[10px] font-bold text-slate-400">{label}</div>
      <div className="text-sm font-bold text-slate-800 mt-0.5">{value || '-'}</div>
    </div>
  </div>
);

const TenantDetailsDialog: React.FC<TenantDetailsDialogProps> = ({ tenant, onClose }) => {
  const [attachments, setAttachments] = useState<any[]>([]);
  const [isLoadingAttachments, setIsLoadingAttachments] = useState(false);

  useEffect(() => {
    if (tenant.id) {
      setIsLoadingAttachments(true);
      api.attachments.getByEntity('tenant', String(tenant.id))
        .then(data => setAttachments(data))
        .catch(err => console.error("Error loading attachments", err))
        .finally(() => setIsLoadingAttachments(false));
    }
  }, [tenant]);

  const handleViewAttachment = async (id: string, name: string) => {
    try {
      const res = await api.attachments.getData(id);
      if (res && res.file_data) {
        openBlob(res.file_data, name, 'view');
      } else {
        alert("❌ الملف فارغ أو غير موجود");
      }
    } catch (err: any) {
      alert("❌ تعذر عرض المرفق: " + err.message);
    }
  };

  const handleDownloadAttachment = async (id: string, name: string) => {
    try {
      const res = await api.attachments.getData(id);
      if (res && res.file_data) {
        openBlob(res.file_data, name, 'download');
      } else {
        alert("❌ الملف فارغ أو غير موجود");
      }
    } catch (err: any) {
      alert("❌ تعذر تحميل المرفق: " + err.message);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-fadeIn">
      <div className="bg-white rounded-3xl shadow-2xl border border-slate-200 w-full max-w-4xl overflow-hidden flex flex-col max-h-[90vh] animate-scaleIn">
        
        {/* Header */}
        <div className="bg-slate-50 p-6 border-b border-slate-200 flex justify-between items-center shrink-0">
          <div>
            <h2 className="text-xl font-black text-slate-800 flex items-center gap-2">
              <User className="text-fly-600 w-5 h-5" />
              <span>تفاصيل بيانات المستأجر</span>
            </h2>
            <p className="text-[10px] text-slate-400 mt-1">عرض السجل الكامل للعقد والمرفقات التابعة له</p>
          </div>
          <button onClick={onClose} className="p-2 bg-white text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all border border-slate-100">
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6 custom-scrollbar bg-slate-50/50">
          
          {/* Main Info Card */}
          <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm space-y-6">
            <div className="flex items-center justify-between pb-4 border-b border-slate-100">
              <h3 className="font-bold text-slate-800 flex items-center gap-2">
                <User size={18} className="text-fly-600" />
                <span>المعلومات الشخصية والاتصال</span>
              </h3>
              <span className={`px-3 py-1 rounded-full text-xs font-black ${
                tenant.type === 'Individual' ? 'bg-blue-50 text-blue-700' : 'bg-purple-50 text-purple-700'
              }`}>
                {tenant.type === 'Individual' ? 'مستأجر فرد' : 'مستأجر شركة'}
              </span>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <DetailItem label="الاسم الكامل" value={`${tenant.firstName} ${tenant.lastName}`} icon={User} />
              <DetailItem label="رقم الهاتف" value={<span dir="ltr">{tenant.mobile}</span>} icon={Phone} />
              <DetailItem label="البريد الإلكتروني" value={tenant.email} icon={Mail} />
              <DetailItem label="رقم البطاقة المدنية" value={tenant.civilId} icon={FileText} />
              <DetailItem label="اللغة المفضلة" value={tenant.language === 'Arabic' ? 'العربية' : 'English'} icon={FileText} />
              <DetailItem 
                label="الوضع القانوني" 
                value={tenant.hasLegalCase ? (
                  <span className="text-red-600 font-bold flex items-center gap-1"><ShieldAlert size={14} /> يوجد قضية قانونية</span>
                ) : (
                  <span className="text-green-600 font-bold">سليم (لا توجد قضايا)</span>
                )} 
                icon={ShieldAlert} 
              />
            </div>
          </div>

          {/* Contract Details Card */}
          <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm space-y-6">
            <h3 className="font-bold text-slate-800 flex items-center gap-2 pb-4 border-b border-slate-100">
              <Calendar size={18} className="text-indigo-600" />
              <span>تفاصيل عقد التأجير</span>
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <DetailItem label="تاريخ بداية العقد" value={tenant.contractStart ? new Date(tenant.contractStart).toLocaleDateString('ar-KW') : '-'} icon={Calendar} />
              <DetailItem label="تاريخ نهاية العقد" value={tenant.contractEnd ? new Date(tenant.contractEnd).toLocaleDateString('ar-KW') : '-'} icon={Calendar} />
              <DetailItem label="دورة الدفع" value={tenant.paymentCycle === 'Monthly' ? 'شهري' : tenant.paymentCycle === 'Quarterly' ? 'ربع سنوي' : 'سنوي'} icon={Calendar} />
              <DetailItem label="يوم سداد الإيجار" value={`${tenant.rentPaymentDay || 1} من كل شهر`} icon={Calendar} />
            </div>
          </div>

          {/* Assigned Units Card */}
          <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm space-y-4">
            <h3 className="font-bold text-slate-800 flex items-center gap-2 pb-4 border-b border-slate-100">
              <Briefcase size={18} className="text-purple-600" />
              <span>الوحدات المؤجرة ({tenant.assignedUnits?.length || 0})</span>
            </h3>
            
            {tenant.assignedUnits && tenant.assignedUnits.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {tenant.assignedUnits.map((unit: any, idx: number) => (
                  <div key={idx} className="flex justify-between items-center p-4 bg-slate-50 border border-slate-150 rounded-xl">
                    <div>
                      <div className="font-bold text-slate-800 text-sm">{unit.propertyName}</div>
                      <div className="text-xs text-slate-400 font-medium mt-1">رقم الوحدة: {unit.unitNumber}</div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="font-black text-slate-800">{Number(unit.rent).toLocaleString()} د.ك</div>
                      <div className="text-[10px] text-slate-400 font-bold mt-1">الإيجار التعاقدي</div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6 text-slate-400 text-xs italic">
                هذا المستأجر غير مسكن في أي وحدة حالياً.
              </div>
            )}
          </div>

          {/* Contract Attachments Card */}
          <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm space-y-4">
            <h3 className="font-bold text-slate-800 flex items-center gap-2 pb-4 border-b border-slate-100">
              <Paperclip size={18} className="text-slate-600" />
              <span>مرفقات ومستندات عقد الإيجار</span>
            </h3>
            
            {isLoadingAttachments ? (
              <div className="flex items-center justify-center py-10 text-slate-400 gap-2">
                <Loader2 className="animate-spin text-fly-600" size={20} />
                <span className="text-xs">جاري تحميل المرفقات...</span>
              </div>
            ) : attachments.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {attachments.map((att) => (
                  <div key={att.id} className="flex items-center justify-between p-3.5 bg-slate-50 border border-slate-250 rounded-xl hover:border-slate-300 transition-colors">
                    <div className="flex items-center gap-3 truncate">
                      <div className="bg-white p-2.5 rounded-lg border border-slate-200 text-slate-400 shrink-0">
                        <FileText size={16} />
                      </div>
                      <div className="truncate">
                        <div className="text-xs font-bold text-slate-800 truncate" title={att.file_name}>{att.file_name}</div>
                        <div className="text-[9px] text-slate-400 font-mono mt-0.5">{new Date(att.uploaded_at || att.uploadedAt || Date.now()).toLocaleDateString('en-GB')}</div>
                      </div>
                    </div>
                    
                    <div className="flex gap-1.5 shrink-0">
                      <button
                        onClick={() => handleViewAttachment(att.id, att.file_name)}
                        className="p-2 bg-white text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
                        title="معاينة المستند"
                      >
                        <Eye size={14} />
                      </button>
                      <button
                        onClick={() => handleDownloadAttachment(att.id, att.file_name)}
                        className="p-2 bg-white text-fly-600 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
                        title="تحميل الملف"
                      >
                        <Download size={14} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 bg-slate-50/50 rounded-xl border border-dashed border-slate-200 text-slate-400 text-xs">
                لا توجد أي صور أو مرفقات مرفوعة لعقد هذا المستأجر.
              </div>
            )}
          </div>
          
        </div>
        
        {/* Footer */}
        <div className="p-4 bg-white border-t border-slate-200 flex justify-center shrink-0">
          <button onClick={onClose} className="px-12 py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-xl transition-all text-sm">
            إغلاق النافذة
          </button>
        </div>

      </div>
    </div>
  );
};

export default TenantDetailsDialog;
